
import { Component, ElementRef, EventEmitter, Output, ViewChild , signal } from '@angular/core';
import { AccountService } from 'src/app/services/account/account.service';
import { ImageProcessingService } from 'src/app/services/account/image-processing.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { environment } from 'src/environments/environment';

@Component({
  standalone: true,
  selector: 'app-avatar',
  templateUrl: './avatar.component.html',
  imports: [],
  styleUrl: './avatar.component.scss'
})
export class AvatarComponent {
  @ViewChild('avatarInput') avatarInput!: ElementRef<HTMLInputElement>;

  public avatarFile: File | null = null;
  public readonly avatarImg = signal<string | null>(null);
  public readonly avatarLoading = signal<boolean>(false);
  public readonly invalidImgSize = signal<boolean>(false);
  public readonly invalidImgType = signal<boolean>(false);

  @Output() avatarProcessing = new EventEmitter();

  constructor(private imageProcessingService: ImageProcessingService, private accountService: AccountService, public languageService: LanguageService) {
  }

  public async onAvatarChange(event: Event): Promise<void> {
    this.invalidImgSize.set(false);
    this.invalidImgType.set(false);

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const isValidType = this.imageProcessingService.isSupportedUploadImage(file);

    this.invalidImgType.set(!isValidType);

    if (!isValidType) {
      input.value = '';
      return;
    }

    this.avatarLoading.set(true);
    this.avatarProcessing.emit(true);

    try {
      const processedFile = await this.imageProcessingService.prepareAvatarUpload(file);

      this.revokeAvatarPreviewUrl();

      this.avatarFile = processedFile;
      this.avatarImg.set(URL.createObjectURL(processedFile));
    } catch (error) {
      console.error('Avatar processing failed:', error);
      this.invalidImgType.set(true);
      input.value = '';
    } finally {
      this.avatarLoading.set(false);
      this.avatarProcessing.emit(false);
    }
  }

  public deleteAvatarImg(): void {
    this.revokeAvatarPreviewUrl();
    this.avatarImg.set(null);
    this.avatarFile = null;
    this.avatarLoading.set(false);

    if (this.avatarInput?.nativeElement) {
      this.avatarInput.nativeElement.value = '';
    }
  }

  public async initAvatar(): Promise<void> {
    const userAvatar = this.accountService.getUserAvatar();
    if (!userAvatar) {
      return;
    }

    this.avatarLoading.set(true);

    try {
      const avatarUrl = await this.imageProcessingService.resizeImageUrlToSquare(
        `${environment.AMAZON_S3_BUCKET_URL}/${userAvatar}`,
        180
      );

      this.revokeAvatarPreviewUrl();
      this.avatarImg.set(avatarUrl);
    } catch (error) {
      console.error('Avatar init failed:', error);
      this.avatarImg.set(null);
    } finally {
      this.avatarLoading.set(false);
    }
  }

  public getAvatarFile(): File | string{
    if (this.accountService.getUserAvatar() && this.avatarImg() == null){
      return "delete";
    }

    return this.avatarFile!;
  }

  private revokeAvatarPreviewUrl(): void {
    if (this.avatarImg()?.startsWith('blob:')) {
      URL.revokeObjectURL(this.avatarImg()!);
    }
  }

  public ngOnDestroy(): void {
    this.revokeAvatarPreviewUrl();
  }
}
